import React, { useContext, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { ViewportContext } from '../../viewport';

import { IconHeartFilled } from '@prisjakt/ui/icon/heart-filled';
import { IconHeartOutlined } from '@prisjakt/ui/icon/heart-outlined';
import { ListModal } from 'src/areas/product/components/product-header/components/action-buttons/list-modal';
import BaseButton from '../base-button';
import LoginPromoModal from '../../modal/login-promo';
import {
  addListItemRequest,
  addItemToMyProductsRequest,
} from '../../../areas/sidebar/actions/lists';

import { useIsAuthenticated } from '../../../hooks/use-is-authenticated';

import type { AppState } from '../../../reducers';
import type {
  TooltipAlign,
  TooltipPlacement,
} from 'src/components/tooltip-new';

type List = { id: number; name: string; inventory: boolean };

type Me = {
  lists: {
    nodes: List[];
  };
};

type Props = {
  dropdownGrow?: 'left' | 'right';
  productId: number;
  productName: string;
  tooltipAlign?: TooltipAlign;
  tooltipPlacement?: TooltipPlacement;
};

export const AddToListButton = ({
  productId,
  productName,
  tooltipAlign,
  tooltipPlacement,
}: Props) => {
  const { t } = useAppContext();
  const { isDesktop } = useContext(ViewportContext);

  const dispatch = useDispatch();

  const [showLoginPromo, setLoginPromoState] = useState(false);

  const toggleLoginPromo = (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    setLoginPromoState(!showLoginPromo);
  };

  // Highlights the heart if the product was recently added to a list.
  // Kind of an ugly hack; should be fixed once we can get this information
  // from GraphQL.
  const [addedToList, setAddedToListState] = useState<boolean>(false);
  const [listOpen, setListOpen] = useState(false);

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const isAuthenticated = useIsAuthenticated();
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );
  const latestLists: List[] = me?.lists?.nodes
    .filter(list => deletedLists[list.id] !== true)
    .filter(list => !list.inventory)
    .sort(a => {
      return a.id === selectedList ? 1 : 0;
    });

  const addToMyProducts = useCallback(() => {
    dispatch(addItemToMyProductsRequest(productId, productName, isDesktop));
    setAddedToListState(true);
  }, [dispatch, isDesktop, productId, productName]);

  const addToList = useCallback(() => {
    dispatch(addListItemRequest(productId, productName, isDesktop));
    setAddedToListState(true);
  }, [dispatch, isDesktop, productId, productName]);

  if (!productId) return null;

  if (isAuthenticated) {
    return (
      <>
        {listOpen && (
          <ListModal
            addToList={addToList}
            addToMyProducts={addToMyProducts}
            isOpen={listOpen}
            closeModal={() => setListOpen(false)}
            lists={latestLists}
            isDesktop={Boolean(isDesktop)}
          />
        )}
        <BaseButton
          isActive={addedToList}
          icon={addedToList ? <IconHeartFilled /> : <IconHeartOutlined />}
          ariaLabel={t('common', 'button.save')}
          onClick={() => {
            return !latestLists || latestLists.length === 0
              ? addToList()
              : setListOpen(true);
          }}
          tooltip={t('product', 'action-button.list-tooltip')}
          tooltipAlign={tooltipAlign}
          tooltipPlacement={tooltipPlacement}
        />
      </>
    );
  }
  return (
    <>
      {showLoginPromo && (
        <LoginPromoModal
          isOpen={showLoginPromo}
          onRequestClose={toggleLoginPromo}
        />
      )}
      <BaseButton
        isActive={addedToList}
        icon={addedToList ? <IconHeartFilled /> : <IconHeartOutlined />}
        ariaLabel={t('common', 'button.save')}
        onClick={toggleLoginPromo}
        tooltip={t('product', 'action-button.list-tooltip')}
        tooltipAlign={tooltipAlign}
        tooltipPlacement={tooltipPlacement}
      />
    </>
  );
};
